import {
  Button,
  FormField,
  GDSInputStatus,
  GDSStyleObject,
  Icon,
  Popover,
  TextInput,
} from '@leagueplatform/genesis-core';
import { CalendarView } from '@leagueplatform/web-common-components';
import React, { useState, useCallback, useEffect } from 'react';
import MaskedInput from 'react-text-mask';
import { isEmpty, isInvalidDate } from '../helpers/helpers';
import './grid-calendar.css';
import CalendarIcon from '../../assets/calendar_white.svg';

interface CalendarProps {
  popoverOpen: boolean;
  setPopoverOpen: Function;
  popoverOnOpenChange: Function;
  label: string;
  inputStatus?: GDSInputStatus;
  statusMessage?: string;
  required?: boolean;
  formFieldCss?: GDSStyleObject;
  isTextBoxDisabled?: boolean;
  selectedDate: any;
  setSelectedDate: any;
  popoverContentAlign?: any;
  popoverContentAlignOffset?: any;
  popoverContentSide?: any;
  popoverContentSideOffset?: number;
  popoverContentAvoidCollisions?: boolean;
  minLimit?: any;
  maxLimit?: any;
  isCalendarButtonEnabled?: boolean;
  isCalendarDisabled?: boolean;
  calendarPopoverCss?: GDSStyleObject;
}

export function GridCalendar(props: CalendarProps) {
  const {
    popoverOpen,
    setPopoverOpen,
    popoverOnOpenChange,
    label,
    inputStatus,
    statusMessage,
    required,
    formFieldCss,
    selectedDate,
    setSelectedDate,
    isTextBoxDisabled,
    popoverContentAlign,
    popoverContentAlignOffset,
    popoverContentSide,
    popoverContentSideOffset,
    popoverContentAvoidCollisions,
    minLimit,
    maxLimit,
    isCalendarButtonEnabled = true,
    isCalendarDisabled,
    calendarPopoverCss,
  } = props;

  const dateSelected = (e: any) => {
    const day = e.day.toString().padStart(2, '0');
    const month = e.month.toString().padStart(2, '0');
    const formatDate = `${month}/${day}/${e.year}`;
    setSelectedDate(formatDate);
    setPopoverOpen(false);
  };

  const dateTyped = (e: any) => {
    setSelectedDate(e);
  };

  const hasDefaultError = useState(undefined);

  const defaultValidation = useCallback(() => {
    const errorStatus: GDSInputStatus = 'error';

    if (!isEmpty(selectedDate) && selectedDate.charAt(0) !== '_') {
      // default error handling, checks if it is a valid calendar date
      const formatDate = selectedDate.split('/');
      const dateToNum = formatDate.map(Number);
      if (
        dateToNum.includes(NaN) || // accounts for underscores in input mask
        isInvalidDate(selectedDate)
      )
        return errorStatus;
    }
    return undefined;
  }, [selectedDate]);

  useEffect(() => {
    setPopoverOpen(false);
  }, [setPopoverOpen, selectedDate]);

  return (
    <Popover.Root
      open={popoverOpen}
      onOpenChange={() => {
        popoverOnOpenChange();
      }}
    >
      <FormField
        id="gridCalendarTextLabel"
        label={label}
        inputStatus={inputStatus || defaultValidation()}
        statusMessage={
          statusMessage || (hasDefaultError && 'Please enter a valid date.')
        }
        required={required}
        css={formFieldCss}
      >
        <MaskedInput
          mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
          onChange={(e: any) => {
            dateTyped(e.target.value);
          }}
          render={(ref: any, properties: any) => (
            <TextInput
              ref={(input: any) => ref(input)}
              placeholder="MM/DD/YYYY"
              id="masked-input-date"
              disabled={isTextBoxDisabled}
              className="gridCalendarTextInput"
              value={selectedDate}
              onChange={properties.onChange}
              addOnEnd={
                isCalendarButtonEnabled && (
                  <Popover.Trigger>
                    <Button
                      className="gridCalendarTextInputButton"
                      css={{
                        '&.GDS-button.gridCalendarTextInputButton:focus': {
                          boxShadow:
                            'inset 0 0 0 $borderWidths$innerFocus white',
                        },
                        borderRadius: '0 $radii$medium $radii$medium 0',
                        paddingLeft: '$one',
                        paddingRight: '$one',
                      }}
                    >
                      {' '}
                      <Icon
                        icon={CalendarIcon as string}
                        size="$one"
                        tint="$interactiveIconDefault"
                      />
                    </Button>
                  </Popover.Trigger>
                )
              }
            />
          )}
        />
      </FormField>
      <Popover.Content
        css={{ zIndex: '$modal' }}
        align={popoverContentAlign}
        alignOffset={popoverContentAlignOffset}
        side={popoverContentSide}
        sideOffset={popoverContentSideOffset || (defaultValidation() ? 25 : 4)}
        avoidCollisions={popoverContentAvoidCollisions}
      >
        <FormField id="gridCalendar" label="Grid calendar popover" hideLabel>
          <CalendarView
            minValue={minLimit}
            maxValue={maxLimit}
            isDisabled={isCalendarDisabled}
            onChange={(e) => {
              dateSelected(e);
            }}
            css={calendarPopoverCss}
          />
        </FormField>
      </Popover.Content>
    </Popover.Root>
  );
}
